import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import {
  OsceCategories,
  RATING_CONFIG_NAMES,
  type ConversationType,
  type ExamType,
  type OSCECategory,
  type RatingConfigName,
  type TaskType,
} from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import { instructionCategoryResolver, isRatedAtLeastOnceResolver, osceBeforeUpdate } from '../server/resolver.js';
import { InstructionCategory } from './instructionCategory.js';
import { OSCEText } from './osceText.js';

@Entity()
export class OSCE extends MongoEntity {
  static override _class = Names.OSCE._class;
  static override _collectionName = Names.OSCE._collectionName;

  constructor(doc: PartialEntityData<OSCE>) {
    super(doc);
  }

  static override get allFields() {
    return selectionSet(() => OSCE, 3);
  }

  static get narrowAllFields() {
    return selectionSet(() => OSCE, 3, ['isRatedAtLeastOnce', 'instructionCategories']);
  }

  @validate(nonOptional())
  @Field(type => String)
  examType!: ExamType;

  @Field(type => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @Field(type => Date)
  archivedAt?: Maybe<Date>;

  @validate(nonOptional())
  @Field(type => String)
  title!: string;

  @Field(type => String)
  titleTrainee: Maybe<string>;

  @validate(({ value, key, target }: ValidationFunctionOptions<OSCECategory, OSCE>) => {
    if (value && !OsceCategories.includes(value))
      throw new ValidationError(`${value} ist kein gültiger OSCE Typ`, { key, instance: target });
  })
  @Field(type => String)
  osceType: Maybe<OSCECategory>;

  @Field(type => String, { default: v => v ?? RATING_CONFIG_NAMES[1] })
  ratingConfigName: Maybe<RatingConfigName>;

  @validate(({ value, key, target }: ValidationFunctionOptions<number, OSCE>) => {
    if (Math.round(value) !== value)
      throw new ValidationError('Die Dauer (in Minuten) darf keine Kommazahl sein', { instance: target, key });
  })
  @Field(type => Int, { default: v => v ?? 10 })
  durationInMinutes!: number;

  /** duration in seconds */
  @validate(({ value, key, target }: ValidationFunctionOptions<number, OSCE>) => {
    if (value >= target.durationInMinutes * 60)
      throw new ValidationError('Die Vorbereitungszeit darf nicht länger dauern als die Gesamt-Zeit');
  })
  @Field(type => Int, { default: v => v ?? 0 })
  startButtonDuration!: number;

  @validate(nonOptional())
  @Field(type => [InstructionCategory], {
    default: v => v ?? [],
    resolve: function (this: OSCE) {
      return instructionCategoryResolver(this);
    },
    includeInParameter: true,
  })
  instructionCategories!: Maybe<InstructionCategory[]>;

  @Field(type => OSCEText, { default: v => v ?? new OSCEText({}) })
  text: Maybe<OSCEText>;

  @Field(type => Boolean, { default: v => v ?? true })
  enableVideo!: boolean;

  @Field(type => ID)
  videoFileID: Maybe<string>;

  @validate(nonOptional())
  @Field(type => [String], { default: v => v ?? [] })
  files!: string[];

  @validate(({ value, key, target }: ValidationFunctionOptions<TaskType, OSCE>) => {
    if (value !== 'voice' && value !== 'text')
      throw new ValidationError(`Der Stationstyp ist weder 'voice' noch 'text'.`, { key, instance: target });
  })
  @Field(type => String)
  taskType!: TaskType;

  @Field(type => String)
  conversationType: Maybe<ConversationType>;

  @Field(type => Boolean, {
    resolve: function (this: OSCE | EntityData<OSCE>) {
      return isRatedAtLeastOnceResolver(this);
    },
  })
  isRatedAtLeastOnce: Maybe<boolean>;

  static override async beforeUpdate(oldEntity: EntityData<OSCE>, update: OSCE) {
    await osceBeforeUpdate(oldEntity, update);
  }
}
